/**
 * voiceoverService.js
 *
 * Plays language-specific voiceover audio when a POI (tag) is clicked.
 * Audio files live at:
 *   public/assets/audio/voiceover/{tagId}/{language}.mp3
 * e.g.
 *   public/assets/audio/voiceover/h00dMaDyCpp/mr.mp3   ← Marathi
 *   public/assets/audio/voiceover/h00dMaDyCpp/en.mp3   ← English
 *   public/assets/audio/voiceover/h00dMaDyCpp/hi.mp3   ← Hindi
 *
 * Missing files are silently ignored — the background music is never ducked
 * unless the audio loaded successfully (handled inside audioService).
 *
 * Usage:
 *   voiceoverService.playForTag('h00dMaDyCpp', 'mr')
 *   voiceoverService.stop()
 *   voiceoverService.isPlaying()
 */

import audioService from './audioService';

class VoiceoverService {
  constructor() {
    this._currentTagId  = null;
    this._currentLang   = null;
    this._playing       = false;
    // Resume-from-pause cache. When proximity-triggered narration is
    // interrupted (user walks away), we capture how far into the clip
    // we got so the same tag/lang can pick up where it left off if the
    // user returns within `RESUME_WINDOW_MS`. Cleared on natural end.
    //   { tagId, lang, offset (seconds), at (Date.now()) }
    this._resumeCache = null;

    window._voiceoverService = this;
  }

  /**
   * Play the voiceover for a given tag in the given language.
   *
   * @param {string} tagId    – Matterport tag / POI id
   * @param {string} language – 'mr' | 'en' | 'hi'
   * @param {object} [callbacks]
   * @param {Function} [callbacks.onStart]  – called when audio actually begins playing
   * @param {Function} [callbacks.onEnded]  – called when audio finishes
   * @param {boolean}  [callbacks.unmute]   – if true and audio is currently
   *                                          muted, unmute first (used by
   *                                          explicit user-gesture play
   *                                          buttons so a tap on "play
   *                                          narration" actually produces
   *                                          sound and reflects in the
   *                                          header mute toggle).
   */
  async playForTag(tagId, language, callbacks = {}) {
    if (!tagId) return;

    // Honour a caller-supplied unmute hint. Only unmute when the call site
    // is a real user gesture (POI click, modal play button, editor preview)
    // — proximity-triggered auto-narration intentionally respects mute.
    if (callbacks.unmute === true) {
      try {
        if (audioService.isMuted?.()) audioService.setMuted(false);
      } catch (_) { /* ignore */ }
    }

    // Cross-system handoff: only ONE narration plays at a time. If the
    // tour-narration path (footer quick-nav / guided tour) is currently
    // playing, fade it out smoothly before the new POI voiceover begins
    // so the user never hears two voices overlap.
    try {
      if (audioService.getCurrentNarrationKey?.()) {
        audioService.fadeOutTourNarration?.(0.4);
      }
    } catch (_) { /* ignore */ }

    const lang = language || 'mr';
    // Tour-stop narrations are stored under a different path and use
    // plain language codes (no en_tts variant). They are addressable via
    // their `narrationKey` (e.g. "stop_1_mandir_entrance"), which the
    // POI editor exposes as a selectable voiceover option.
    const isTourStop = typeof tagId === 'string' && /^stop_\d+_/.test(tagId);
    const audioLang = isTourStop ? lang : (lang === 'en' ? 'en_tts' : lang);
    const src = isTourStop
      ? `/assets/audio/narration/${tagId}_standalone_${audioLang}.mp3`
      : `/assets/audio/voiceover/${tagId}/${audioLang}.mp3`;

    // Resume-from-pause: if the same tag+lang was interrupted within
    // the resume window, start from the captured offset. Returning
    // within 10 s picks up mid-narration; after that the user must
    // click the speaker icon on the POI label to start narration
    // again — preventing surprise auto-resume after a longer absence.
    const RESUME_WINDOW_MS = 10_000;
    let startOffset = 0;
    if (
      this._resumeCache
      && this._resumeCache.tagId === tagId
      && this._resumeCache.lang === lang
      && this._resumeCache.offset > 0.5
      && Date.now() - this._resumeCache.at < RESUME_WINDOW_MS
    ) {
      startOffset = this._resumeCache.offset;
      // eslint-disable-next-line no-console
      console.log(`🎙️ VoiceoverService: resuming "${tagId}" [${lang}] from ${startOffset.toFixed(1)}s`);
    }
    // Clear the cache now that we've consumed it (or chose not to).
    this._resumeCache = null;

    this._currentTagId = tagId;
    this._currentLang  = lang;
    this._playing      = true;

    await audioService.playVoiceover(src, {
      fadeOutDuration: 2,
      fadeInDuration:  3,
      voiceoverVolume: 0.92,
      bgDuckedLevel:   0.03, // ~3% bg-music whisper under the narration
      startOffset,
      onStart: callbacks.onStart,
      onEnded: () => {
        this._playing = false;
        // Natural end — no resume needed.
        this._resumeCache = null;
        if (typeof callbacks.onEnded === 'function') callbacks.onEnded();
        // eslint-disable-next-line no-console
        console.log(`🎙️ VoiceoverService: finished — tag "${tagId}" [${lang}]`);
      },
    });

    // If playVoiceover returned without starting (missing file), reset state
    if (!audioService._voiceoverSource) {
      this._playing = false;
    }
  }

  /** Stop the currently playing voiceover and restore background music. */
  stop() {
    if (!this._playing && !audioService._voiceoverDucked) return;
    // Capture playback position BEFORE stopping the source so we can
    // resume from here if the user returns within the resume window.
    const elapsed = typeof audioService.getVoiceoverElapsed === 'function'
      ? audioService.getVoiceoverElapsed() : 0;
    if (this._currentTagId && elapsed > 0.5) {
      this._resumeCache = {
        tagId:  this._currentTagId,
        lang:   this._currentLang,
        offset: elapsed,
        at:     Date.now(),
      };
    }
    this._playing      = false;
    this._currentTagId = null;
    this._currentLang  = null;
    audioService.stopVoiceover();
  }

  /**
   * Slowly fade out the current voiceover (e.g. when the user walks away
   * from a POI) instead of cutting it abruptly. Captures the resume offset
   * up front so a quick return picks up mid-narration. Mark `_playing`
   * false immediately so re-entry triggers in NavigationHints can decide
   * to start a fresh playback (which will cancel this fade cleanly via
   * audioService.stopVoiceover → cancels the pending fade timer).
   *
   * @param {number} [durationSec=2.5] – fade-out duration
   */
  fadeOut(durationSec = 2.5) {
    if (!this._playing && !audioService._voiceoverDucked) return;
    const elapsed = typeof audioService.getVoiceoverElapsed === 'function'
      ? audioService.getVoiceoverElapsed() : 0;
    if (this._currentTagId && elapsed > 0.5) {
      this._resumeCache = {
        tagId:  this._currentTagId,
        lang:   this._currentLang,
        offset: elapsed,
        at:     Date.now(),
      };
    }
    this._playing      = false;
    this._currentTagId = null;
    this._currentLang  = null;
    if (typeof audioService.fadeOutVoiceover === 'function') {
      audioService.fadeOutVoiceover(durationSec);
    } else {
      audioService.stopVoiceover();
    }
  }

  /** Manually clear the resume cache (e.g. on language change). */
  clearResume() {
    this._resumeCache = null;
  }

  /** Returns true while a voiceover is actively playing. */
  isPlaying() {
    return this._playing;
  }

  /** Currently playing POI tag id (or null). */
  getCurrentTagId() {
    return this._playing ? this._currentTagId : null;
  }

  /** Language code of the currently playing voiceover (or null). */
  getCurrentLang() {
    return this._playing ? this._currentLang : null;
  }
}

const voiceoverService = new VoiceoverService();
export default voiceoverService;
